import { Component, OnInit, Input, Output, EventEmitter, isDevMode } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NgbModal, ModalDismissReasons, NgbTabTitle } from '@ng-bootstrap/ng-bootstrap';
import { DeliveryFormModalComponent } from './delivery-form-modal/delivery-form-modal.component';
import { ConfirmDeletionModalComponent } from '../../../confirm-deletion-modal/confirm-deletion-modal.component';

import { Delivery } from 'src/classes/delivery';
import { Contact } from 'src/classes/contact';
import { Task } from 'src/classes/task';
import { isActiveFilter, extractFilterFromDeliveryJSON } from '../../../../utils/extractFilterFromDeliveryJSON';

let nextDeliveryId = -1;

interface DeliveryData {
  idDelivery: number;
  Subject: string;
  Message: string;
  Tags: string[];
  Contacts: number[];
  Method: string;
  Filters: string;
}

@Component({
  selector: 'app-task-step-delivery',
  templateUrl: './task-step-delivery.component.html',
  styleUrls: [
    './task-step-delivery.component.scss',
    './../task-form.component.scss'
  ]
})
export class TaskStepDeliveryComponent implements OnInit {

  @Input() task: Task;
  @Input() formGroup: FormGroup = new FormGroup({});
  @Input() contacts: Contact[] = [];
  @Input() availableTags: {tag: string, count: number}[] = [];
  @Output() addDelivery = new EventEmitter<Delivery>();
  @Output() updateDelivery = new EventEmitter<Delivery>();
  @Output() deleteDelivery = new EventEmitter<number>();
  closeResult: string;

  constructor(private modalService: NgbModal) {}

  ngOnInit() {
    /* DEBUG */ // this.onAddDeliveryClick();
    /* DEBUG */ // this.onEditDeliveryClick(this.deliveries[0]);
  }

  public get deliveries() {
    return this.formGroup.get('Deliveries').value;
  }

  getFilterLabel(filterStr: string, type: 'count' | 'tooltip'): string {
    try {
      if (!filterStr) {
        return type === 'count' ? 'None' : '';
      }
      const filters = JSON.parse(filterStr);
      if (filters instanceof Array && type === 'count') {
        const activeFilters = filters.filter(isActiveFilter);
        return activeFilters.length === 0 ? 'None' : `${activeFilters.length} ${activeFilters.length === 1 ? 'filter' : 'filters'}`;
      }
      return extractFilterFromDeliveryJSON(filterStr);
    } catch(err) {
      if (isDevMode()) {
        console.log(err.stack);
      }
    }
    return '?';
  }

  getTagString(tags: string | string[]) {
    if (!tags) {
      return "---";
    }
    if (typeof tags === "string") {
      return tags;
    }
    return tags.join(" ");
  }

  async onDeleteDeliveryClick(deliveryData: {idDelivery: number}) {
    if (await this.showConfirmDeletionModal() === true) {
      this.deleteDelivery.emit(deliveryData.idDelivery);
    }
  }

  async showConfirmDeletionModal(): Promise<boolean> {
    try {
      const modalRef = this.modalService.open(ConfirmDeletionModalComponent, { ariaLabelledBy: 'modal-confirm-deletion' });
      const result = await modalRef.result;
      return result === true;
    } catch(reason) {
    }
    return false;
  }

  async showDeliveryFormModal(data: DeliveryData | null) {
    const isNew = (data === null || data === undefined);
    try {
      const modalRef = this.modalService.open(DeliveryFormModalComponent, { ariaLabelledBy: 'modal-add-delivery' });
      modalRef.componentInstance.contactList = this.contacts;
      modalRef.componentInstance.availableTags = this.availableTags;

      if (!isNew) {
        const delivery = new Delivery();
        delivery.idDelivery = data.idDelivery;
        delivery.subject = data.Subject;
        delivery.message = data.Message;
        delivery.contactId = data.Contacts;
        delivery.tags = typeof data.Tags === "string" ? (data.Tags as string).split(" ") : data.Tags;
        delivery.type = data.Method;
        delivery.filters = data.Filters;
        modalRef.componentInstance.delivery = delivery;
      }

      modalRef.componentInstance.workspaceId = this.task.PBIWorkspaceId;
      modalRef.componentInstance.reportId = this.task.PBIReportId;

      const result = (await modalRef.result);

      if (result) {
        // delivery id should not have changed
        const delivery = new Delivery();
        delivery.subject = result.Subject;
        delivery.message = result.Message;
        delivery.type = result.ReportType;
        delivery.tags = typeof result.Tags === "string" ? result.Tags.split(" ") : result.Tags;
        delivery.contactId = result.Contacts;
        delivery.filters = result.Filters;
        if (isNew) {
          delivery.idDelivery = nextDeliveryId--;
          this.addDelivery.emit(delivery);
        } else {
          delivery.idDelivery = data.idDelivery;
          this.updateDelivery.emit(delivery);
        }
      }
    } catch(reason) {
    }
  }

  async onEditDeliveryClick(deliveryData: DeliveryData) {
    await this.showDeliveryFormModal(deliveryData);
  }

  async onAddDeliveryClick() {
    await this.showDeliveryFormModal(null);
  }

  getContactNames(ids: number[]) {
    if (!(this.contacts instanceof Array) || this.contacts.length === 0) {
      return ids;
    }
    const list = ids.map(id => {
      const contactElem = this.contacts.find(contact => contact.idContact.toString() === id.toString());
      return contactElem ? contactElem.name : undefined;
    }).filter(el => el !== undefined);
    if (list.length > 5) {
      list.splice(5, list.length - 5, '...');
    }
    return list;
  }
}
